import { useMemo, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "../../utilities/helpers";
import { useCurrency } from "../dropdown";
import {
	formatCompactCurrency,
	formatCurrency,
	formatSignedCurrency,
	formatSignedPercent,
	humanizeAssetLabel,
	normalizeAsset,
	useAssetMarketData,
	useAssetPalette,
	useInvestmentSummaryQuery,
} from "./shared";

const MINUTE_IN_MS = 1000 * 60;
// A quote older than this is flagged: markets are closed at most ~3 days in a row.
const STALE_AFTER_MS = MINUTE_IN_MS * 60 * 24 * 3;
// Selling a whole position leaves float dust like 1e-14 units.
const UNIT_EPSILON = 1e-9;

const COLUMNS = [
	{ key: "asset", label: "Asset", numeric: false },
	{ key: "price", label: "Price", numeric: true },
	{ key: "value", label: "Value", numeric: true },
	{ key: "costBasis", label: "Cost basis", numeric: true },
	{
		key: "unrealized",
		label: "Unrealized P/L",
		numeric: true,
		hint: "Market value minus the remaining cost basis of the units you still hold. The percentage is measured against that cost basis.",
	},
	{
		key: "totalReturn",
		label: "Total return",
		numeric: true,
		hint: "Unrealized P/L plus realized gains from sales and dividends received. Shown without a percentage: realized gains came from capital that is no longer in the position.",
	},
	{ key: "weight", label: "Weight", numeric: true },
];

const formatAge = (ms) => {
	const minutes = Math.max(0, Math.round(ms / MINUTE_IN_MS));
	if (minutes < 1) return "just now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.round(minutes / 60);
	if (hours < 48) return `${hours}h ago`;
	return `${Math.round(hours / 24)}d ago`;
};

const formatUnits = (units) =>
	new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(units);

const formatSignedCompactCurrency = (value, currency) => {
	const amount = Number(value || 0);
	const formatted = formatCompactCurrency(Math.abs(amount), currency);
	if (Math.abs(amount) < 0.005) return formatted;
	return `${amount > 0 ? "+" : "−"}${formatted}`;
};

const toneClass = (value) => {
	if (value > 0.005) return "text-emerald-700";
	if (value < -0.005) return "text-rose-700";
	return "text-slate-600";
};

const SortIcon = ({ active, direction }) => {
	if (!active) {
		return <ChevronsUpDown aria-hidden="true" className="h-3.5 w-3.5 opacity-40" />;
	}
	const Icon = direction === "asc" ? ArrowUp : ArrowDown;
	return <Icon aria-hidden="true" className="h-3.5 w-3.5" />;
};

const Overview = () => {
	const { assetByDate, assets } = useAssetMarketData();
	const { currency } = useCurrency();
	const paletteFor = useAssetPalette();
	const [sort, setSort] = useState({ key: "value", direction: "desc" });
	const [showClosed, setShowClosed] = useState(false);
	const { data: summary, isLoading } = useInvestmentSummaryQuery();

	const rows = useMemo(() => {
		const summaryByAsset = {};
		(summary?.assets || []).forEach((assetSummary) => {
			summaryByAsset[normalizeAsset(assetSummary.asset)] = assetSummary;
		});
		const now = Date.now();
		const totalWorth = summary?.totals?.currentWorth || 0;

		return assets.map((asset) => {
			const row = summaryByAsset[asset];
			const quote = assetByDate?.[asset];
			const units = Number(row?.openUnits || 0);
			const rawPrice = quote?.value ?? row?.currentPrice;
			const tickAt = quote?.createdAt ? new Date(quote.createdAt) : null;
			const tickAge = tickAt && !Number.isNaN(tickAt.getTime()) ? now - tickAt.getTime() : null;
			const worth = row?.currentWorth || 0;
			const costBasis = row?.remainingCostBasis || 0;
			const unrealized = row?.unrealizedPL || 0;

			return {
				asset,
				label: humanizeAssetLabel(asset),
				isOpen: Math.abs(units) > UNIT_EPSILON,
				units,
				averageCost: row?.averageCost || 0,
				price: rawPrice != null ? Number(rawPrice) : null,
				tickAt: tickAge != null ? tickAt : null,
				tickAge,
				isStale: tickAge != null && tickAge > STALE_AFTER_MS,
				value: worth,
				costBasis,
				unrealized,
				unrealizedPercent: costBasis > 0 ? (unrealized / costBasis) * 100 : null,
				totalReturn: row?.totalReturn || 0,
				realized: row?.realizedPL || 0,
				dividends: row?.dividendIncome || 0,
				weight: totalWorth > 0 ? (worth / totalWorth) * 100 : 0,
			};
		});
	}, [assetByDate, assets, summary]);

	const closedCount = rows.filter((row) => !row.isOpen).length;
	const openCount = rows.length - closedCount;

	const visibleRows = useMemo(() => {
		const { key, direction } = sort;
		const factor = direction === "asc" ? 1 : -1;
		return rows
			.filter((row) => showClosed || row.isOpen)
			.sort((left, right) => {
				if (key === "asset") return factor * left.label.localeCompare(right.label);
				const diff = (left[key] ?? -Infinity) - (right[key] ?? -Infinity);
				return factor * diff || left.label.localeCompare(right.label);
			});
	}, [rows, showClosed, sort]);

	const totals = useMemo(() => {
		const source = summary?.totals || {};
		const costBasis = source.remainingCostBasis || 0;
		return {
			value: source.currentWorth || 0,
			costBasis,
			unrealized: source.unrealizedPL || 0,
			unrealizedPercent:
				costBasis > 0 ? ((source.unrealizedPL || 0) / costBasis) * 100 : null,
			totalReturn: source.totalReturn || 0,
			realized: source.realizedPL || 0,
			dividends: source.dividendIncome || 0,
		};
	}, [summary]);

	const handleSort = (column) => {
		setSort((current) =>
			current.key === column.key
				? { key: column.key, direction: current.direction === "asc" ? "desc" : "asc" }
				: { key: column.key, direction: column.numeric ? "desc" : "asc" }
		);
	};

	const sortedColumn = COLUMNS.find((column) => column.key === sort.key);

	const renderReturnBreakdown = (realized, dividends) => {
		const parts = [];
		if (Math.abs(realized) >= 0.5) {
			parts.push(`${formatSignedCompactCurrency(realized, currency)} realized`);
		}
		if (Math.abs(dividends) >= 0.5) {
			parts.push(`${formatSignedCompactCurrency(dividends, currency)} div`);
		}
		if (!parts.length) return null;
		return <div className="text-xs text-slate-500">incl. {parts.join(" · ")}</div>;
	};

	return (
		<section className="rounded-xl border border-slate-200 bg-white shadow-sm">
			<div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between md:px-6 md:py-5">
				<div>
					<h2 className="text-lg font-semibold text-slate-900">Holdings</h2>
					<p className="text-sm text-slate-500">
						{openCount} open {openCount === 1 ? "position" : "positions"}, marked to
						the latest price.
					</p>
				</div>
				{closedCount > 0 && (
					<button
						type="button"
						role="switch"
						aria-checked={showClosed}
						onClick={() => setShowClosed((current) => !current)}
						className="inline-flex h-9 w-fit shrink-0 items-center gap-2.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
					>
						<span
							aria-hidden="true"
							className={cn(
								"relative inline-block h-4 w-7 rounded-full transition-colors",
								showClosed ? "bg-slate-900" : "bg-slate-200"
							)}
						>
							<span
								className={cn(
									"absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
									showClosed && "translate-x-3"
								)}
							/>
						</span>
						Show closed positions ({closedCount})
					</button>
				)}
			</div>

			<div className="overflow-x-auto border-t border-slate-200">
				<table className="w-full min-w-[940px] border-collapse text-sm">
					<caption className="sr-only">
						Holdings sorted by {sortedColumn?.label.toLowerCase()},{" "}
						{sort.direction === "asc" ? "ascending" : "descending"}
					</caption>
					<thead>
						<tr className="border-b border-slate-200 text-xs text-slate-500">
							{COLUMNS.map((column, index) => {
								const active = sort.key === column.key;
								return (
									<th
										key={column.key}
										scope="col"
										aria-sort={
											active
												? sort.direction === "asc"
													? "ascending"
													: "descending"
												: "none"
										}
										className={cn(
											"px-4 py-2 font-medium first:pl-4 md:first:pl-6 last:pr-4 md:last:pr-6",
											column.numeric ? "text-right" : "text-left",
											index === 0 &&
												"sticky left-0 z-10 bg-white shadow-[inset_-1px_0_0_#e2e8f0] md:shadow-none"
										)}
									>
										<button
											type="button"
											onClick={() => handleSort(column)}
											title={column.hint}
											className={cn(
												"-mx-1.5 inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 font-medium transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
												column.numeric && "flex-row-reverse",
												active && "text-slate-900"
											)}
										>
											<span
												className={cn(
													column.hint &&
														"underline decoration-slate-300 decoration-dotted underline-offset-4"
												)}
											>
												{column.label}
											</span>
											<SortIcon active={active} direction={sort.direction} />
										</button>
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{isLoading &&
							!summary &&
							Array.from({ length: 4 }).map((_, index) => (
								<tr key={`loading-${index}`} className="border-b border-slate-100">
									<td colSpan={COLUMNS.length} className="px-4 py-3 md:px-6">
										<div className="h-8 animate-pulse rounded bg-slate-100" />
									</td>
								</tr>
							))}
						{visibleRows.map((row) => {
							const palette = paletteFor(row.asset);
							const tickTitle = row.tickAt
								? `Last price ${row.tickAt.toLocaleString()}`
								: undefined;

							return (
								<tr
									key={row.asset}
									className={cn(
										"group border-b border-slate-100 transition-colors hover:bg-slate-50",
										!row.isOpen && "text-slate-500"
									)}
								>
									<th
										scope="row"
										className="sticky left-0 z-10 bg-white py-3 pl-4 pr-4 text-left font-normal shadow-[inset_-1px_0_0_#e2e8f0] transition-colors group-hover:bg-slate-50 md:pl-6 md:shadow-none"
									>
										<div className="flex items-center gap-2.5">
											<span
												aria-hidden="true"
												className={cn("h-2.5 w-2.5 shrink-0 rounded-full", palette.dot)}
											/>
											<div className="min-w-0">
												<div className="font-medium text-slate-900">{row.label}</div>
												<div className="whitespace-nowrap text-xs tabular-nums text-slate-500">
													{row.isOpen
														? `${formatUnits(row.units)} units · avg ${formatCurrency(
																row.averageCost,
																currency
														  )}`
														: "Closed position"}
												</div>
											</div>
										</div>
									</th>
									<td className="px-4 py-3 text-right">
										<div className="tabular-nums text-slate-900">
											{row.price != null ? formatCurrency(row.price, currency) : "—"}
										</div>
										{row.tickAt &&
											(row.isStale ? (
												<div
													className="flex items-center justify-end gap-1 whitespace-nowrap text-xs font-medium text-amber-700"
													title={tickTitle}
												>
													<AlertTriangle aria-hidden="true" className="h-3 w-3" />
													Stale · {formatAge(row.tickAge)}
												</div>
											) : (
												<div
													className="whitespace-nowrap text-xs text-slate-500"
													title={tickTitle}
												>
													updated {formatAge(row.tickAge)}
												</div>
											))}
									</td>
									<td className="px-4 py-3 text-right font-medium tabular-nums text-slate-900">
										{formatCurrency(row.value, currency)}
									</td>
									<td className="px-4 py-3 text-right tabular-nums text-slate-600">
										{formatCurrency(row.costBasis, currency)}
									</td>
									<td className={cn("px-4 py-3 text-right tabular-nums", toneClass(row.unrealized))}>
										<div className="font-medium">
											{formatSignedCurrency(row.unrealized, currency)}
										</div>
										{row.unrealizedPercent != null && (
											<div className="text-xs">{formatSignedPercent(row.unrealizedPercent)}</div>
										)}
									</td>
									<td className="px-4 py-3 text-right tabular-nums">
										<div className={cn("font-medium", toneClass(row.totalReturn))}>
											{formatSignedCurrency(row.totalReturn, currency)}
										</div>
										{renderReturnBreakdown(row.realized, row.dividends)}
									</td>
									<td className="py-3 pl-4 pr-4 md:pr-6">
										<div className="flex items-center justify-end gap-2.5">
											<div
												aria-hidden="true"
												className="h-1.5 w-14 overflow-hidden rounded-full bg-slate-100"
											>
												<div
													className="h-full rounded-full bg-slate-400"
													style={{ width: `${Math.min(100, row.weight)}%` }}
												/>
											</div>
											<span className="w-12 text-right tabular-nums text-slate-900">
												{row.weight.toFixed(1)}%
											</span>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
					{summary && (
						<tfoot>
							<tr className="border-t border-slate-200 bg-slate-50 font-medium">
								<th
									scope="row"
									className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 pr-4 text-left text-slate-900 shadow-[inset_-1px_0_0_#e2e8f0] md:pl-6 md:shadow-none"
								>
									Total
								</th>
								<td className="px-4 py-3" />
								<td className="px-4 py-3 text-right tabular-nums text-slate-900">
									{formatCurrency(totals.value, currency)}
								</td>
								<td className="px-4 py-3 text-right tabular-nums text-slate-600">
									{formatCurrency(totals.costBasis, currency)}
								</td>
								<td className={cn("px-4 py-3 text-right tabular-nums", toneClass(totals.unrealized))}>
									<div>{formatSignedCurrency(totals.unrealized, currency)}</div>
									{totals.unrealizedPercent != null && (
										<div className="text-xs font-normal">
											{formatSignedPercent(totals.unrealizedPercent)}
										</div>
									)}
								</td>
								<td className="px-4 py-3 text-right tabular-nums">
									<div className={toneClass(totals.totalReturn)}>
										{formatSignedCurrency(totals.totalReturn, currency)}
									</div>
									<div className="font-normal">
										{renderReturnBreakdown(totals.realized, totals.dividends)}
									</div>
								</td>
								<td className="py-3 pl-4 pr-4 text-right tabular-nums text-slate-900 md:pr-6">
									100.0%
								</td>
							</tr>
						</tfoot>
					)}
				</table>
			</div>
		</section>
	);
};

export default Overview;
